import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  Users, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  DollarSign,
  TrendingUp,
  Activity,
  Zap,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { toast } from 'sonner';
import { format } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [distributing, setDistributing] = useState(false);
  const [simulation, setSimulation] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchSimulation();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSimulation = async () => {
    try {
      const response = await adminAPI.simulateProfit();
      setSimulation(response.data);
    } catch (error) {
      console.error('Failed to fetch simulation:', error);
    }
  };

  const handleDistribute = async () => {
    if (!window.confirm('Are you sure you want to distribute daily profits to all active investments?')) {
      return;
    }
    
    setDistributing(true);
    try {
      const response = await adminAPI.distributeProfit();
      toast.success(`Distributed $${response.data.total_distributed.toLocaleString()} to ${response.data.users_affected} users`);
      fetchDashboard();
      fetchSimulation();
    } catch (error) {
      toast.error('Failed to distribute profits');
    } finally {
      setDistributing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-hotpink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  const statCards = [
    { label: 'Total Users', value: stats.total_users || 0, icon: Users, color: 'cyan', change: `${stats.active_users || 0} active` },
    { label: 'Platform Balance', value: `$${(stats.total_balance || 0).toLocaleString()}`, icon: DollarSign, color: 'emerald' },
    { label: 'Active Trading', value: `$${(stats.total_invested || 0).toLocaleString()}`, icon: TrendingUp, color: 'cyan' },
    { label: 'Total Deposits', value: `$${(stats.total_deposits || 0).toLocaleString()}`, icon: ArrowDownToLine, color: 'emerald' },
    { label: 'Total Withdrawals', value: `$${(stats.total_withdrawals || 0).toLocaleString()}`, icon: ArrowUpFromLine, color: 'hotpink' },
    { label: 'Pending Deposits', value: stats.pending_deposits || 0, icon: AlertCircle, color: 'yellow', alert: stats.pending_deposits > 0 },
    { label: 'Pending Withdrawals', value: stats.pending_withdrawals || 0, icon: AlertCircle, color: 'yellow', alert: stats.pending_withdrawals > 0 },
    { label: 'Daily Rate', value: `${stats.daily_profit_rate || 2}%`, icon: Activity, color: 'cyan' },
  ];

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-6"
      data-testid="admin-dashboard"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white">Admin Dashboard</h1>
          <p className="text-white/60 mt-1">Platform overview and management</p>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div 
        variants={stagger}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            className={`glass-card p-4 ${stat.alert ? 'border-yellow-500/50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                stat.color === 'cyan' ? 'bg-cyan-500/20' :
                stat.color === 'emerald' ? 'bg-emerald-500/20' :
                stat.color === 'hotpink' ? 'bg-hotpink-400/20' :
                'bg-yellow-500/20'
              }`}>
                <stat.icon className={`w-5 h-5 ${
                  stat.color === 'cyan' ? 'text-cyan-400' :
                  stat.color === 'emerald' ? 'text-emerald-400' :
                  stat.color === 'hotpink' ? 'text-hotpink-400' :
                  'text-yellow-400'
                }`} />
              </div>
            </div>
            <p className="text-white/50 text-xs mt-3">{stat.label}</p>
            <p className="text-xl font-mono font-bold text-white">{stat.value}</p>
            {stat.change && <p className="text-xs text-white/40">{stat.change}</p>}
          </motion.div>
        ))}
      </motion.div>

      {/* Profit Distribution */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-outfit font-semibold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Daily Profit Distribution
            </h2>
            <p className="text-white/50 text-sm mt-1">
              Distribute {stats.daily_profit_rate || 2}% daily profit to all active trading positions
            </p>
          </div>
          
          {simulation && (
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-white/50 text-xs">Active Positions</p>
                <p className="text-lg font-mono text-white">{simulation.active_investments}</p>
              </div>
              <div className="text-center">
                <p className="text-white/50 text-xs">Trading Volume</p>
                <p className="text-lg font-mono text-cyan-400">${simulation.total_invested?.toLocaleString()}</p>
              </div>
              <div className="text-center">
                <p className="text-white/50 text-xs">To Distribute</p>
                <p className="text-lg font-mono text-emerald-400">${simulation.total_would_distribute?.toLocaleString()}</p>
              </div>
              <Button
                onClick={handleDistribute}
                disabled={distributing || simulation.active_investments === 0}
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                data-testid="distribute-profits-btn"
              >
                {distributing ? 'Distributing...' : 'Distribute Now'}
              </Button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <h2 className="text-xl font-outfit font-semibold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={() => navigate('/admin/deposits')}
              variant="outline"
              className={`justify-start gap-2 h-auto py-4 ${stats.pending_deposits > 0 ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-white/10'}`}
              data-testid="admin-pending-deposits-btn"
            >
              <ArrowDownToLine className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium">Pending Deposits</p>
                <p className="text-xs text-white/50">{stats.pending_deposits} awaiting</p>
              </div>
            </Button>
            <Button
              onClick={() => navigate('/admin/withdrawals')}
              variant="outline"
              className={`justify-start gap-2 h-auto py-4 ${stats.pending_withdrawals > 0 ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-white/10'}`}
              data-testid="admin-pending-withdrawals-btn"
            >
              <ArrowUpFromLine className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium">Pending Withdrawals</p>
                <p className="text-xs text-white/50">{stats.pending_withdrawals} awaiting</p>
              </div>
            </Button>
            <Button
              onClick={() => navigate('/admin/users')}
              variant="outline"
              className="justify-start gap-2 h-auto py-4 border-white/10"
              data-testid="admin-manage-users-btn"
            >
              <Users className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium">Manage Users</p>
                <p className="text-xs text-white/50">{stats.total_users} total</p>
              </div>
            </Button>
            <Button
              onClick={() => navigate('/admin/settings')}
              variant="outline"
              className="justify-start gap-2 h-auto py-4 border-white/10"
              data-testid="admin-settings-btn"
            >
              <Activity className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium">Platform Settings</p>
                <p className="text-xs text-white/50">Configure rates</p>
              </div>
            </Button>
          </div>
        </motion.div>

        {/* Recent Users */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-outfit font-semibold text-white">Recent Users</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin/users')}
              className="text-cyan-400"
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {dashboard?.recent_users?.slice(0, 5).map((user) => (
              <div 
                key={user.id}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/users/${user.id}`)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Users className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{user.name}</p>
                    <p className="text-white/50 text-xs">{user.email}</p>
                  </div>
                </div>
                <span className={`status-badge ${
                  user.status === 'active' ? 'status-badge-success' :
                  user.status === 'unverified' ? 'status-badge-warning' :
                  'status-badge-danger'
                }`}>
                  {user.status}
                </span>
              </div>
            ))}
            {(!dashboard?.recent_users || dashboard.recent_users.length === 0) && (
              <p className="text-white/40 text-center py-4">No users yet</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Recent Transactions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Deposits */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-outfit font-semibold text-white">Recent Deposits</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin/deposits')}
              className="text-cyan-400"
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {dashboard?.recent_deposits?.slice(0, 5).map((deposit) => (
              <div key={deposit.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div>
                  <p className="text-white text-sm font-medium">{deposit.user_name || 'User'}</p>
                  <p className="text-white/50 text-xs">{deposit.crypto_type}</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 font-mono font-bold">${deposit.amount.toLocaleString()}</p>
                  <span className={`text-xs ${
                    deposit.status === 'approved' ? 'text-emerald-400' :
                    deposit.status === 'rejected' ? 'text-hotpink-400' :
                    'text-yellow-400'
                  }`}>
                    {deposit.status}
                  </span>
                </div>
              </div>
            ))}
            {(!dashboard?.recent_deposits || dashboard.recent_deposits.length === 0) && (
              <p className="text-white/40 text-center py-4">No deposits yet</p>
            )}
          </div>
        </motion.div>

        {/* Recent Withdrawals */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-outfit font-semibold text-white">Recent Withdrawals</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin/withdrawals')}
              className="text-cyan-400"
            >
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {dashboard?.recent_withdrawals?.slice(0, 5).map((withdrawal) => (
              <div key={withdrawal.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div>
                  <p className="text-white text-sm font-medium">{withdrawal.user_name || 'User'}</p>
                  <p className="text-white/50 text-xs">{withdrawal.crypto_type}</p>
                </div>
                <div className="text-right">
                  <p className="text-hotpink-400 font-mono font-bold">-${withdrawal.amount.toLocaleString()}</p>
                  <span className={`text-xs ${
                    withdrawal.status === 'completed' ? 'text-emerald-400' :
                    withdrawal.status === 'rejected' ? 'text-hotpink-400' :
                    'text-yellow-400'
                  }`}>
                    {withdrawal.status}
                  </span>
                </div>
              </div>
            ))}
            {(!dashboard?.recent_withdrawals || dashboard.recent_withdrawals.length === 0) && (
              <p className="text-white/40 text-center py-4">No withdrawals yet</p>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
